import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { getRecentArticles } from '@/data/blog';
import AOS from 'aos';
import 'aos/dist/aos.css';

const BlogPreview = () => {
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: true,
      mirror: false,
    });
  }, []);

  const recentArticles = getRecentArticles(3).map(article => {
    return {
      slug: article.slug,
      date: article.date,
      image: article.image,
      title: article.meta[currentLanguage].title,
      excerpt: article.meta[currentLanguage].description
    };
  });

  const blogPosts = recentArticles.length >= 3 ? recentArticles : [
    {
      title: t.blogTitle1,
      excerpt: t.blogExcerpt1,
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      date: "02.04.2025",
      slug: "video-marketing-trends"
    },
    {
      title: t.blogTitle2,
      excerpt: t.blogExcerpt2,
      image: "https://images.unsplash.com/photo-1558655146-6c222b05fce4?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      date: "28.03.2025",
      slug: "ai-marketing"
    },
    {
      title: t.blogTitle3,
      excerpt: t.blogExcerpt3,
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=500&q=80",
      date: "15.03.2025",
      slug: "erfolgreiche-videokampagnen"
    }
  ];

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      {/* Improved video background container */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <iframe 
            allow="fullscreen;autoplay" 
            allowFullScreen 
            src="https://streamable.com/e/1ha84o?autoplay=1&muted=1&nocontrols=1&loop=1" 
            width="100%" 
            height="100%" 
            style={{
              border: "none", 
              width: "100%", 
              height: "100%", 
              position: "absolute", 
              left: "0px", 
              top: "0px", 
              overflow: "hidden",
              pointerEvents: "none",
              objectFit: "cover",
              filter: "brightness(1.2) contrast(1.1)" // Enhance video visibility
            }}
            title="Blog Background Video"
          ></iframe>
        </div>
        
        {/* Reduced opacity overlay */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] z-0"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 text-white glow-effect">{t.latestBlogPosts}</h2>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">
            {t.blogSubtitle}
          </p>
        </div>
        
        {/* Improved grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogPosts.map((post, index) => (
            <article 
              key={index} 
              className="glass-card rounded-xl overflow-hidden transition-transform duration-300 hover:translate-y-[-4px] backdrop-blur-md bg-black/30 border border-purple-500/30"
              data-aos="fade-up" 
              data-aos-delay={index * 100 + 100}
            >
              <Link to={`/${currentLanguage}/blog/${post.slug}`}>
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-40 object-cover"
                />
              </Link>
              <div className="p-5">
                <span className="text-sm text-purple-300 mb-1 block">{post.date}</span>
                <h3 className="text-lg font-semibold mb-2 text-white">
                  <Link to={`/${currentLanguage}/blog/${post.slug}`} className="hover:text-futurelevel-purple transition-colors">
                    {post.title}
                  </Link>
                </h3>
                <p className="text-gray-300 mb-3 text-sm">
                  {post.excerpt}
                </p>
                <Link 
                  to={`/${currentLanguage}/blog/${post.slug}`} 
                  className="text-purple-300 hover:text-purple-400 font-medium flex items-center text-sm"
                >
                  {t.readMore}
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    className="w-4 h-4 ml-1"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M14 5l7 7m0 0l-7 7m7-7H3" 
                    />
                  </svg>
                </Link>
              </div>
            </article>
          ))}
        </div>
        
        <div className="text-center mt-10" data-aos="fade-up" data-aos-delay="500">
          <Link to={getLocalizedPath('blog')} className="button-outline inline-block bg-black/40 border-purple-400/60 text-white hover:bg-purple-900/60 hover:border-purple-500">
            {t.viewAllPosts}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
